import { create } from 'zustand';
import { HookTrackWizardData, WizardStep } from './types';

interface HookTrackWizardStore {
  currentStep: number;
  steps: WizardStep[];
  data: HookTrackWizardData;
  
  // Actions
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setTestInfo: (name: string, description: string) => void;
  setMainVideo: (video: HookTrackWizardData['mainVideo']) => void;
  toggleIntro: (intro: HookTrackWizardData['selectedIntros'][0]) => void;
  reorderIntros: (intros: HookTrackWizardData['selectedIntros']) => void;
  setTrafficMode: (mode: 'auto' | 'manual') => void;
  updatePercentage: (introId: string, percentage: number) => void;
  setTrackingEnabled: (enabled: boolean) => void;
  setTrackingPlatform: (slug: string | null, platformId: number | null) => void;
  setTrackingToken: (token: string) => void;
  validateCurrentStep: () => boolean;
  completeStep: (stepId: number) => void;
  reset: () => void;
}

const initialSteps: WizardStep[] = [
  { 
    id: 1, 
    title: 'Vídeo Principal', 
    description: 'Escolha o vídeo que será exibido após as introduções', 
    completed: false 
  },
  { 
    id: 2, 
    title: 'Intros em Teste', 
    description: 'Selecione as introduções que participarão do teste', 
    completed: false 
  },
  {
    id: 3,
    title: 'Distribuição do Tráfego',
    description: 'Defina como o público será dividido',
    completed: false
  },
  {
    id: 4,
    title: 'Rastreamento',
    description: 'Integre com uma plataforma de conversão (opcional)',
    completed: false
  }
];

const initialData: HookTrackWizardData = {
  testName: '',
  testDescription: '',
  mainVideo: null,
  selectedIntros: [],
  trafficDistribution: {
    mode: 'auto',
    percentages: {}
  },
  trackingIntegration: {
    enabled: false,
    platformSlug: null,
    platformId: null,
    platformToken: ''
  }
};

export const useHookTrackWizardStore = create<HookTrackWizardStore>((set, get) => {
  // Reset state on page load (F5)
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      // This will cause the store to reset on next load
    });
  }

  return {
    currentStep: 1,
    steps: initialSteps,
    data: initialData,
  
  setCurrentStep: (step) => set({ currentStep: step }),
  
  nextStep: () => {
    const { currentStep, steps } = get();
    if (currentStep < steps.length) {
      set({ currentStep: currentStep + 1 });
    }
  },
  
  prevStep: () => {
    const { currentStep } = get();
    if (currentStep > 1) {
      set({ currentStep: currentStep - 1 });
    }
  },
  
  setTestInfo: (name, description) => {
    set((state) => ({
      data: { ...state.data, testName: name, testDescription: description }
    }));
  },
  
  setMainVideo: (video) => {
    set((state) => {
      // Se o vídeo selecionado como principal estava nas intros, removê-lo
      const filteredIntros = video
        ? state.data.selectedIntros.filter(i => i.id !== video.id)
        : state.data.selectedIntros;

      // Recalcular percentuais se alguma intro foi removida
      let newPercentages = state.data.trafficDistribution.percentages;
      if (filteredIntros.length !== state.data.selectedIntros.length && filteredIntros.length > 0) {
        newPercentages = {};
        const equalPercentage = Math.floor(100 / filteredIntros.length);
        filteredIntros.forEach((intro, index) => {
          newPercentages[intro.id] = index === filteredIntros.length - 1
            ? 100 - equalPercentage * (filteredIntros.length - 1)
            : equalPercentage;
        });
      }

      return {
        data: {
          ...state.data,
          mainVideo: video,
          selectedIntros: filteredIntros,
          trafficDistribution: {
            mode: state.data.trafficDistribution.mode,
            percentages: newPercentages
          }
        },
        steps: state.steps.map(step =>
          step.id === 1 ? { ...step, completed: video !== null } : step
        )
      };
    });
  },
  
  toggleIntro: (intro) => {
    set((state) => {
      const exists = state.data.selectedIntros.find(i => i.id === intro.id);
      const newIntros = exists
        ? state.data.selectedIntros.filter(i => i.id !== intro.id)
        : [...state.data.selectedIntros, intro];
      
      // Always distribute equally (automatic distribution)
      const newPercentages: Record<string, number> = {};
      const equalPercentage = Math.floor(100 / newIntros.length);
      newIntros.forEach((i, index) => {
        newPercentages[i.id] = index === newIntros.length - 1
          ? 100 - equalPercentage * (newIntros.length - 1) // Last one gets remainder
          : equalPercentage;
      });
      
      return {
        data: {
          ...state.data,
          selectedIntros: newIntros,
          trafficDistribution: {
            mode: 'auto',
            percentages: newPercentages
          }
        },
        steps: state.steps.map(step => 
          step.id === 2 ? { ...step, completed: newIntros.length >= 2 } : step
        )
      };
    });
  },
  
  reorderIntros: (intros) => {
    set((state) => ({
      data: {
        ...state.data,
        selectedIntros: intros
      }
    }));
  },

  setTrafficMode: (mode) => {
    set((state) => {
      if (mode === 'auto') {
        // Auto mode - distribute equally
        const newPercentages: Record<string, number> = {};
        const equalPercentage = Math.floor(100 / state.data.selectedIntros.length);
        state.data.selectedIntros.forEach((intro, index) => {
          newPercentages[intro.id] = index === state.data.selectedIntros.length - 1
            ? 100 - equalPercentage * (state.data.selectedIntros.length - 1)
            : equalPercentage;
        });
        
        return {
          data: {
            ...state.data,
            trafficDistribution: { mode: 'auto', percentages: newPercentages }
          }
        };
      } else {
        // Manual mode - keep current percentages
        return {
          data: {
            ...state.data,
            trafficDistribution: { ...state.data.trafficDistribution, mode: 'manual' }
          }
        };
      }
    });
  },
  
  updatePercentage: (introId, percentage) => {
    set((state) => {
      const newPercentages = { ...state.data.trafficDistribution.percentages };
      newPercentages[introId] = Math.max(0, Math.min(100, percentage));
      
      // Auto-adjust to ensure sum is 100
      const total = Object.values(newPercentages).reduce((a, b) => a + b, 0);
      const isValid = total === 100;
      
      return {
        data: {
          ...state.data,
          trafficDistribution: {
            ...state.data.trafficDistribution,
            percentages: newPercentages
          }
        },
        steps: state.steps.map(step => 
          step.id === 3 ? { ...step, completed: isValid } : step
        )
      };
    });
  },
  
  setTrackingEnabled: (enabled) => {
    set((state) => ({
      data: {
        ...state.data,
        trackingIntegration: {
          ...state.data.trackingIntegration,
          enabled,
          // Ao desligar, limpa plataforma/token para não submeter resíduo
          ...(enabled ? {} : { platformSlug: null, platformId: null, platformToken: '' })
        }
      }
    }));
  },

  setTrackingPlatform: (slug, platformId) => {
    set((state) => ({
      data: {
        ...state.data,
        trackingIntegration: {
          ...state.data.trackingIntegration,
          platformSlug: slug,
          platformId
        }
      }
    }));
  },

  setTrackingToken: (token) => {
    set((state) => ({
      data: {
        ...state.data,
        trackingIntegration: {
          ...state.data.trackingIntegration,
          platformToken: token
        }
      }
    }));
  },

  validateCurrentStep: () => {
    const { currentStep, data } = get();

    switch (currentStep) {
      case 1:
        return data.mainVideo !== null;
      case 2:
        return data.selectedIntros.length >= 2;
      case 3:
        const total = Object.values(data.trafficDistribution.percentages).reduce((a, b) => a + b, 0);
        return total === 100;
      case 4: {
        // Etapa opcional: válida se o usuário não quer rastrear,
        // ou se escolheu plataforma e informou o token.
        const t = data.trackingIntegration;
        return !t.enabled || (t.platformId !== null && t.platformToken.trim().length > 0);
      }
      default:
        return false;
    }
  },
  
  completeStep: (stepId) => {
    set((state) => ({
      steps: state.steps.map(step => 
        step.id === stepId ? { ...step, completed: true } : step
      )
    }));
  },
  
  reset: () => {
    set({
      currentStep: 1,
      steps: initialSteps,
      data: initialData
    });
  }
}});

